/**
 *@description 自己封装的上传文件组件
 *@author cy
 *@date 2022-05-18 13:52
 **/
import React, { useRef } from "react";
import { Button, Col, message, Row, Typography, Upload } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./index.less";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

/**
 * limitType： 限制文件的 格式
 * file: 文件
 * limitSize: 文件限制大小（MB）
 * limitFileNameLength: 限制文件名长度
 * limitFileName: 文件名中不应包含字符
 **/
const beforeUploadLimit = (
  limitType: Array<string>,
  file: any,
  limitSize?: number | "none",
  limitFileNameLength?: number,
  limitFileName?: Array<string>
) => {
  if (limitSize !== "none") {
    let fileSize = limitSize ? limitSize : 40;
    const isLtLimitSize = file.size / 1024 / 1024 < fileSize;
    // 限制文件大小
    if (!isLtLimitSize) {
      message.error({
        content: "文件不能超过 " + fileSize + " MB",
        key: "fileSize",
      });
      return Upload.LIST_IGNORE;
    }
  }
  // console.log(file, limitType);
  // 限制文件格式
  let fileSuf = file.name.split(".");
  let suffix = fileSuf[fileSuf.length - 1].toLowerCase();
  if (limitType && limitType.indexOf("." + suffix) === -1) {
    message.error({
      content: "文件限" + limitType.join("、") + "格式",
      key: "fileType",
    });
    return Upload.LIST_IGNORE;
  }
  let nameLength = limitFileNameLength ? limitFileNameLength : 100;
  // 限制文件名长度
  if (file.name.length > nameLength) {
    message.error({
      content: "文件名长度不能超过 " + nameLength + " 字",
      key: "fileLength",
    });
    return Upload.LIST_IGNORE;
  }
  let nameLimit = limitFileName ? limitFileName : ["&", "+", "=", "#", "%"];
  // 限制文件名中不应包含字符
  for (let i = 0; i < nameLimit.length; i++) {
    const item = nameLimit[i];
    if (file.name.indexOf(item) !== -1) {
      message.error({
        content: "文件名中不应包含字符 " + nameLimit.join(" ") + " 字符",
        key: "fileCode",
      });
      return Upload.LIST_IGNORE;
    }
  }
  return true;
};

enum EFileStatus {
  ready,
  uploading = "uploading",
  success = "success",
  done = "done",
  error = "error",
  thumbnailPath = "",
}

interface IProps {
  onChange: (fileList: Array<any>) => void;
  showFileList: Array<any>;
  multiple?: boolean; // 是否多文件
  disbaled?: boolean; // 是否禁用
  maxCount?: number; // 最多选择文件
  onRemove?: (file: any) => void; // 删除文件
  fileSpan?: number; // 单个文件占的flex大小
  uploadItemClass?: string;
  listType?: "text" | "picture" | "length" | "filelist";
  accept?: any; // 文件格式
  children?: React.ReactNode;
}

const MyUpload = (props: IProps) => {
  const { t } = useTranslation();
  const {
    disbaled = false,
    multiple = false,
    maxCount = 1,
    onRemove,
    onChange,
    showFileList = [],
    fileSpan = 24,
    uploadItemClass,
    listType = "text",
    accept,
    children,
  } = props;
  const inputRef: any = useRef();
  const fileBeforeShow = (file: any) => {
    let index = showFileList.findIndex((item: any) => {
      return item.name === file.name;
    });
    if (index > -1) {
      message.error({ key: "sameName", content: "图片不能重名" });
      return Upload.LIST_IGNORE;
    }
    return beforeUploadLimit(accept, file, "none", 200);
  };
  const fileChange = (e: any) => {
    let files = inputRef.current.files;
    let dayValue = dayjs().valueOf();
    let canChooseNum = maxCount - showFileList.length; // 限制文件数量
    let newFileList: Array<any> = [];
    for (let fileIndex in files) {
      if (
        files.hasOwnProperty(fileIndex) &&
        fileBeforeShow(files[fileIndex]) === true &&
        newFileList.length < canChooseNum
      ) {
        let sourceObj = {
          status: EFileStatus.ready,
          uid: dayValue + "-" + fileIndex,
          thumbnailPath: "",
        };
        // 若是图片形式展示，需要设置缩略图
        if (listType === "picture" || listType === "filelist") {
          let url = URL.createObjectURL(files[fileIndex]);
          sourceObj.thumbnailPath = url;
        }
        let obj = Object.assign(files[fileIndex], sourceObj);
        newFileList.push(obj);
      } else {
        if (
          files.hasOwnProperty(fileIndex) &&
          fileBeforeShow(files[fileIndex]) === true &&
          maxCount === 1
        ) {
          newFileList = [];
          let sourceObj = {
            status: EFileStatus.ready,
            uid: dayValue + "-" + fileIndex,
            thumbnailPath: "",
          };
          // 若是图片形式展示，需要设置缩略图
          if (listType === "picture" || listType === "filelist") {
            let url = URL.createObjectURL(files[fileIndex]);
            sourceObj.thumbnailPath = url;
          }
          let obj = Object.assign(files[fileIndex], sourceObj);
          newFileList.push(obj);
        }
      }
    }
    if (maxCount === 1) {
      onChange([...newFileList]);
    } else {
      onChange([...showFileList, ...newFileList]);
    }
  };
  const onFileRemove = (file: any) => {
    // const list = [...showFileList];
    // let fileIndex = list.findIndex((item: any) => item.uid === file.uid);
    // if (fileIndex > -1) {
    //   list.splice(fileIndex, 1);
    //   onChange([...list]);
    // }
    inputRef.current.value = "";

    onRemove && onRemove(file);
  };
  const inputClick = () => {
    inputRef.current.click();
  };
  return (
    <div>
      <div>
        <div className={uploadItemClass}>
          {listType !== "picture" &&
            listType !== "filelist" &&
            (children ? (
              <div onClick={inputClick}>{children}</div>
            ) : (
              <>
                <Button
                  disabled={showFileList.length >= maxCount || disbaled}
                  onClick={inputClick}
                >
                  上传文件
                </Button>
                {/* <Text style={{ marginLeft: 5 }}>
                  已选择 {showFileList.length} 张图片
                </Text> */}
              </>
            ))}
          <input
            type="file"
            style={{ display: "none" }}
            id="inputFile"
            multiple={multiple}
            onChange={fileChange}
            ref={inputRef}
            accept={accept}
            disabled={disbaled}
          />
        </div>
        {listType === "picture" && (
          <div className="my-upload-picture-wrap">
            <div className="my-upload-select-picture-card">
              {children ? (
                <div
                  onClick={inputClick}
                  className={`${listType === "picture" ? "my-upload" : ""}`}
                >
                  {children}
                </div>
              ) : (
                <>
                  <Button
                    disabled={showFileList.length >= maxCount}
                    onClick={inputClick}
                  >
                    上传文件
                  </Button>
                  {/* <Text style={{ marginLeft: 5 }}>
                    已选择 {showFileList.length} 张图片
                  </Text> */}
                </>
              )}
            </div>
            {showFileList.map((item: any) => (
              <div key={item.uid} className="my-upload-item-picture-div">
                <div
                  className={`my-upload-list-picture ${
                    item.status === EFileStatus.success && "my-upload-item-done"
                  }
               ${item.status === EFileStatus.error && "my-upload-item-error"}`}
                >
                  <div className="my-upload-item-picture">
                    <img
                      src={item.thumbnailPath}
                      width="100%"
                      height="auto"
                      style={{ maxHeight: "100%", objectFit: "contain" }}
                    />
                    <Button
                      className="my-upload-item-pic-btn"
                      icon={<DeleteOutlined style={{ color: "#fff" }} />}
                      onClick={() => onFileRemove(item)}
                      title={t("upload.delete")}
                    >
                      {t("upload.uploadBtnText")}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {listType === "text" && (
          <Row wrap={true} className="my-upload">
            {showFileList.map((item: any) => (
              <Col
                span={fileSpan}
                key={item.uid}
                className="my-upload-list-item"
              >
                <Text
                  className={`my-upload-item-span ${
                    item.status === EFileStatus.success && "my-upload-item-done"
                  } ${
                    item.status === EFileStatus.error && "my-upload-item-error"
                  }`}
                >
                  {item.name}
                </Text>
                <Button
                  className="my-upload-item-btn"
                  icon={<DeleteOutlined />}
                  onClick={() => onFileRemove(item)}
                  title={t("upload.delete")}
                >
                  {t("upload.uploadBtnText")}
                </Button>
              </Col>
            ))}
          </Row>
        )}

        {listType === "filelist" && (
          <div className="my-upload-picture-wrap filelist">
            <div className="my-upload-select-picture-card">
              {children ? (
                <div
                  onClick={inputClick}
                  className={`${listType === "filelist" ? "my-upload" : ""}`}
                >
                  {children}
                </div>
              ) : (
                <>
                  <Button
                    disabled={showFileList.length >= maxCount}
                    onClick={inputClick}
                  >
                    上传文件
                  </Button>
                  {/* <Text style={{ marginLeft: 5 }}>
                    已选择 {showFileList.length} 张图片
                  </Text> */}
                </>
              )}
            </div>

            {/* <Text style={{ marginBottom: 5, display: "block" }}>
              已选择 {showFileList.length} 个文件
            </Text> */}
            <div className="filelist-list">
              {showFileList.map((item: any) => (
                <div key={item.uid} className="my-upload-item-picture-div">
                  <div
                    className={`my-upload-list-picture ${
                      item.status === EFileStatus.success &&
                      "my-upload-item-done"
                    }
               ${item.status === EFileStatus.error && "my-upload-item-error"}`}
                  >
                    <div className="my-upload-item-picture">
                      {/* <img
                      src={item.thumbnailPath}
                      width="100%"
                      height="auto"
                      style={{ maxHeight: "100%", objectFit: "contain" }}
                    /> */}
                      <div></div>
                      <div className="name">{item.name}</div>
                      <Button
                        className="my-upload-item-pic-btn"
                        icon={<DeleteOutlined style={{ color: "#555" }} />}
                        onClick={() => onFileRemove(item)}
                        title={t("upload.delete")}
                      >
                        {t("upload.uploadBtnText")}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default MyUpload;
